import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Col, Drawer, Row, Spin, notification } from 'antd';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { AppContext } from 'context/AppContext';
import { useComments } from 'services/CommentService';
import { Submission } from 'services/types/TaskTypes';
import { Comment } from 'services/types/CommentTypes';
import SubmissionItem from '../Task/components/SubmissionItem';
import CommentItem from './CommentItem';
import './Comment.css';

interface CommentDrawerProps {
  isOpen: boolean;
  closeHandler: () => void;
  submission: Submission;
  onLikeUpdate?: (updatedSubmission: Submission) => void;
}

const CommentDrawer: React.FC<CommentDrawerProps> = ({
  isOpen,
  closeHandler,
  submission,
  onLikeUpdate,
}) => {
  const isXs = window.innerWidth < 576;
  const { t } = useTranslation();
  const { authentication } = useContext(AppContext);
  const { authKey } = authentication;

  const [currentPage, setCurrentPage] = useState(1);
  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasLoadedSuccessfully, setHasLoadedSuccessfully] = useState(false);

  // Get submission URL for comments API
  const submissionURL = `${process.env.REACT_APP_DOMAIN}/api/submissions/${submission.id}/`;

  // Fetch comments using the service - only when drawer is open and we want to fetch
  const shouldFetch = isOpen && submissionURL && !isLoadingMore;
  const {
    data: commentsData,
    isLoading: isLoadingComments,
    isError: commentsError,
    mutate: mutateComments
  } = useComments(shouldFetch ? submissionURL : '', shouldFetch ? currentPage : 1, shouldFetch ? authKey : '');

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setAllComments([]);
      setHasMore(true);
      setIsLoadingMore(false);
      setHasLoadedSuccessfully(false);
    }
  }, [isOpen, submission.id]);

  // Handle comments data update
  useEffect(() => {
    if (commentsData && isOpen && shouldFetch) {
      setHasLoadedSuccessfully(true);

      const comments = commentsData.results || [];

      if (currentPage === 1) {
        setAllComments(comments);
      } else {
        setAllComments(prev => [...prev, ...comments]);
      }

      // Check if there are more comments to load
      const hasMoreComments = commentsData.next !== null && commentsData.next !== undefined;
      setHasMore(hasMoreComments);
      setIsLoadingMore(false);
    }
  }, [commentsData, currentPage, isOpen, shouldFetch]);

  // Handle errors - only show if we haven't loaded successfully yet
  useEffect(() => {
    if (commentsError && !hasLoadedSuccessfully && isOpen) {
      notification.error({
        message: t('#Comment.error.load_failed_title'),
        description: t('#Comment.error.load_failed'),
      });
      setIsLoadingMore(false);
      setHasMore(false);
    }
  }, [commentsError, hasLoadedSuccessfully, t, isOpen]);

  const onClose = useCallback(() => {
    setCurrentPage(1);
    setAllComments([]);
    setHasMore(true);
    setIsLoadingMore(false);
    setHasLoadedSuccessfully(false);
    closeHandler();
  }, [closeHandler]);

  const loadMoreComments = useCallback(() => {
    if (hasMore && !isLoadingMore && !isLoadingComments && hasLoadedSuccessfully) {
      setIsLoadingMore(true);
      setCurrentPage(prev => prev + 1);
    }
  }, [hasMore, isLoadingMore, isLoadingComments, hasLoadedSuccessfully]);

  const handleCommentLikeUpdate = useCallback((updatedComment: Comment) => {
    setAllComments(prev =>
      prev.map(comment => {
        // Check if this is the comment being updated
        if (comment.id === updatedComment.id) {
          return updatedComment;
        }

        // Check if this comment has child comments that need updating
        if (comment.child_comments && comment.child_comments.length > 0) {
          const updatedChildComments = comment.child_comments.map(childComment =>
            childComment.id === updatedComment.id ? updatedComment : childComment
          );

          // Only update if there was actually a change
          if (updatedChildComments.some((child, index) => child !== comment.child_comments[index])) {
            return {
              ...comment,
              child_comments: updatedChildComments
            };
          }
        }

        return comment;
      })
    );
  }, []);

  const handleCommentAdd = useCallback((newComment: Comment) => {
    // Add new comment to the beginning of the list
    setAllComments(prev => [newComment, ...prev]);

    // Refresh comments to ensure we have the latest data
    mutateComments();
  }, [mutateComments]);

  const handleSubmissionCommentAdd = useCallback((newComment: Comment) => {
    // Handle comment added to the main submission
    handleCommentAdd(newComment);
  }, [handleCommentAdd]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const threshold = 100;

    if (scrollHeight - scrollTop - clientHeight < threshold && hasMore && !isLoadingMore && !isLoadingComments && hasLoadedSuccessfully) {
      loadMoreComments();
    }
  }, [hasMore, isLoadingMore, isLoadingComments, hasLoadedSuccessfully, loadMoreComments]);

  // Show loading only when initially loading and no comments exist
  const showInitialLoading = isLoadingComments && currentPage === 1 && allComments.length === 0;

  // Show no comments message only when not loading, has loaded successfully, and no comments exist
  const showNoComments = !isLoadingComments && !showInitialLoading && hasLoadedSuccessfully && allComments.length === 0;

  return (
    <Drawer
      width={isXs ? undefined : 576}
      height={'100%'}
      placement={isXs ? 'top' : 'right'}
      closable={false}
      open={isOpen}
      onClose={onClose}
      maskClosable={true}
      bodyStyle={{ paddingTop: 0, paddingBottom: 0 }}
      className="comment-drawer"
    >
      {/* Header */}
      <Row justify={'space-between'} className="comment-drawer-header">
        <Col>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        </Col>
        <Col>
          <h3 className="comment-drawer-title">
            {t('#Comment.comments')}
          </h3>
        </Col>
        <Col style={{ width: 32 }} />
      </Row>

      {/* Content */}
      <div className="comment-drawer-content" onScroll={handleScroll}>
        {/* Original Submission with Comment Input */}
        <div className="comment-drawer-submission">
          <SubmissionItem
            item={submission}
            onLikeUpdate={onLikeUpdate}
            onCommentAdd={handleSubmissionCommentAdd}
            isInCommentDrawer={true}
          />
        </div>

        {/* Comments Section */}
        <div className="comment-drawer-comments">
          <div className="comment-section-header">
            <h4>{t('#Comment.comments_list')}</h4>
            {allComments.length > 0 && (
              <span className="comment-count">
                {allComments.length} {t('#Comment.comment', { count: allComments.length })}
              </span>
            )}
          </div>

          {/* Loading state for initial load */}
          {showInitialLoading && (
            <div className="comment-loading">
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                tip={t('#Comment.loading_comments')}
              />
            </div>
          )}

          {/* Comments list */}
          {allComments.length > 0 && (
            <div className="comment-list">
              {allComments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  onLikeUpdate={handleCommentLikeUpdate}
                  onCommentAdd={handleCommentAdd}
                />
              ))}
            </div>
          )}

          {/* Loading more indicator */}
          {isLoadingMore && hasMore && (
            <div className="comment-loading-more">
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />}
                tip={t('#Comment.loading_more')}
              />
            </div>
          )}

          {/* No comments message */}
          {showNoComments && (
            <div className="comment-empty">
              <p>{t('#Comment.no_comments')}</p>
            </div>
          )}

          {/* End of comments message */}
          {!hasMore && allComments.length > 0 && (
            <div className="comment-end">
              <p>{t('#Comment.no_more_comments')}</p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CommentDrawer;